import React, { useContext, useState } from "react";
import ProductCard from "../../components/ProductCard";
import PriceCalculator from "../../components/PriceCalculator";
import EffectsSection from "../../components/EffectsSection";
import SEO from "../../utils/SEO";

// Assets
import acrylic from "../../assets/acrylic.webp";
import aluDuo from "../../assets/alu-duo.webp";
import line from "../../assets/lineweb.webp";
import acrylicCircle from "../../assets/acrylic-circle.webp";
import aluCircle from "../../assets/alu-circle.webp";
import print from "../../assets/shelf.webp";
import a2Print from "../../assets/a2.webp";

// Data
import { products } from "../../data/pricing";

// Language
import { LanguageContext } from "../../context/LanguageContext";
import no from "../../context/no";
import en from "../../context/en";

const TAB_PRODUCTS = {
  fotoprint: ["paper", "a2"],
  acrylic: ["acrylicSquare", "acrylicLine", "acrylicCircle"],
  alu: ["aluSquare", "aluLine", "aluCircle"],
};

const TAB_IDS = ["fotoprint", "acrylic", "alu"];

const productImages = {
  paper: print,
  a2: a2Print,
  acrylicSquare: acrylic,
  acrylicLine: line,
  acrylicCircle: acrylicCircle,
  aluSquare: aluDuo,
  aluLine: line,
  aluCircle: aluCircle,
};

const DigitalNotice = ({ copy }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="mt-4 bg-main-light">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-5 md:p-6">
        <div className="space-y-1">
          <p className="text-main-dark font-copy leading-relaxed">
            {copy.included}
          </p>
          <p className="text-main-dark/70 font-copy text-sm">{copy.buyAlone}</p>
        </div>
        <button
          type="button"
          onClick={() => setIsExpanded((open) => !open)}
          className="cta cta-dark self-start sm:self-center shrink-0 flex items-center gap-2"
          aria-expanded={isExpanded}
        >
          {isExpanded ? copy.hidePrices : copy.showPrices}
          <span className="text-xl" aria-hidden="true">
            {isExpanded ? "−" : "+"}
          </span>
        </button>
      </div>
      {isExpanded && <PriceCalculator productId="digital" />}
    </div>
  );
};

function ProductPage() {
  const { language } = useContext(LanguageContext);
  const currentLang = language === "no" ? no.concept : en.concept;
  const [activeTab, setActiveTab] = useState("fotoprint");

  const activeProducts = TAB_PRODUCTS[activeTab];
  const activeTabCopy = currentLang.tabs[activeTab];

  return (
    <>
      <SEO title={currentLang.title} description={currentLang.photo} />
      <main className="overflow-hidden">
        <EffectsSection />

        <section className="pb-8 md:pb-16">
          <div className="container">
            <div className="wrap">
              <div
                role="tablist"
                aria-label={currentLang.title}
                className="flex border-b border-main-dark/20 mb-6"
              >
                {TAB_IDS.map((tabId) => {
                  const isActive = activeTab === tabId;
                  return (
                    <button
                      key={tabId}
                      type="button"
                      role="tab"
                      id={`product-tab-${tabId}`}
                      aria-selected={isActive}
                      aria-controls={`product-panel-${tabId}`}
                      tabIndex={isActive ? 0 : -1}
                      onClick={() => setActiveTab(tabId)}
                      className={`flex-1 px-3 py-3 font-heading font-bold text-sm sm:text-base md:text-lg transition-colors ${
                        isActive
                          ? "text-main-dark border-b-2 border-main-dark -mb-px"
                          : "text-main-dark/50 hover:text-main-dark/80"
                      }`}
                    >
                      {currentLang.tabs[tabId].label}
                    </button>
                  );
                })}
              </div>

              <div
                role="tabpanel"
                id={`product-panel-${activeTab}`}
                aria-labelledby={`product-tab-${activeTab}`}
              >
                <p className="text-main-dark font-copy leading-relaxed mb-8 max-w-2xl">
                  {activeTabCopy.description}
                </p>

                {activeProducts.map((productId) => {
                  const product = products[productId];
                  return (
                    <ProductCard
                      key={productId}
                      productId={productId}
                      image={productImages[productId]}
                      title={product.name}
                      description={product.description}
                      attributes={product.attributes}
                      imagePosition={
                        productId === "a2" ? "center top" : "center"
                      }
                    />
                  );
                })}
              </div>

              <DigitalNotice copy={currentLang.digitalNotice} />
            </div>
          </div>
        </section>
      </main>
    </>
  );
}

export default ProductPage;
